import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ContactSection = ({ initialService, setInitialService }) => {
  const [formData, setFormData] = useState({
    serviceType: '',
    name: '',
    phone: '',
    location: '',
    message: ''
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialService && serviceOptions.some(s => s.value === initialService)) {
      setFormData(prev => ({ ...prev, serviceType: initialService }));
      setCurrentStep(1); // Ensure form is on the first step
      // Reset the prop so it doesn't re-trigger on subsequent renders
      if (setInitialService) {
        setInitialService('');
      }
    }
  }, [initialService, setInitialService]);

  const serviceOptions = [
    { value: 'perbaikan', label: 'Perbaikan AC (AC tidak dingin, dll)' },
    { value: 'cuci', label: 'Cuci AC (Pembersihan menyeluruh)' },
    { value: 'bongkar-pasang', label: 'Bongkar Pasang AC (Pindahan/Instalasi)' },
    { value: 'maintenance', label: 'Maintenance Rutin (Perawatan berkala)' },
    { value: 'konsultasi', label: 'Konsultasi Gratis (Tanya-tanya dulu)' }
  ];

  const locationOptions = [
    { value: 'jakarta-pusat', label: 'Jakarta Pusat' },
    { value: 'jakarta-selatan', label: 'Jakarta Selatan' },
    { value: 'jakarta-timur', label: 'Jakarta Timur' },
    { value: 'jakarta-barat', label: 'Jakarta Barat' },
    { value: 'jakarta-utara', label: 'Jakarta Utara' },
    { value: 'bogor', label: 'Bogor' },
    { value: 'depok', label: 'Depok' },
    { value: 'tangerang', label: 'Tangerang' },
    { value: 'bekasi', label: 'Bekasi' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1 && formData?.serviceType) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // simulasikan delay pengiriman
    await new Promise(resolve => setTimeout(resolve, 2000));

    // buat pesan WhatsApp
    const message = `Halo AC Service Pro! 🏠\n\n` +
      `Saya ingin menggunakan layanan: *${serviceOptions?.find(s => s?.value === formData?.serviceType)?.label}*\n\n` +
      `📋 Detail:\n` +
      `• Nama: ${formData?.name}\n` +
      `• Telepon: ${formData?.phone}\n` +
      `• Lokasi: ${locationOptions?.find(l => l?.value === formData?.location)?.label}\n` +
      `• Pesan: ${formData?.message || 'Tidak ada pesan tambahan'}\n\n` +
      `Mohon info jadwal kunjungan dan estimasi biaya. Terima kasih! 🙏`;

    window.open(`https://wa.me/6282226648077?text=${encodeURIComponent(message)}`, '_blank'); //ganti nomor dengan nomor bisnis Anda
    
    setIsSubmitting(false);
    
    // Reset form
    setFormData({
      serviceType: '',
      name: '',
      phone: '',
      location: '',
      message: ''
    });
    setCurrentStep(1);
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:+6282226648077';
  };

  const handleDirectWhatsAppClick = () => {
    window.open('https://wa.me/6282226648077', '_blank');
  };

  return (
    <section id="kontak" className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Phone" size={16} className="mr-2" />
              Hubungi Kami
            </div>
            
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6 break-words">
              Siap Membantu Masalah AC Anda
            </h2>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0 break-words">
              Tim teknisi profesional kami siap datang ke lokasi Anda. 
              Konsultasi gratis, harga transparan, garansi kepuasan!
            </p>

            {/* Contact Methods */}
            <div className="space-y-4 mb-8">
              {/* Telepon */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-white rounded-xl shadow-card border border-border">
                <div className="flex items-center w-full sm:w-auto">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <Icon name="Phone" size={24} className="text-accent" />
                  </div>
                  <div className="flex-1 sm:hidden text-left">
                    <h3 className="font-semibold text-foreground">Telepon Langsung</h3>
                    <p className="text-muted-foreground text-sm">Respon cepat dalam 5 menit</p>
                  </div>
                </div>
                <div className="hidden sm:block flex-1">
                  <h3 className="font-semibold text-foreground">Telepon Langsung</h3>
                  <p className="text-muted-foreground text-sm">Respon cepat dalam 5 menit</p>
                </div>
                <Button
                  variant="outline"
                  onClick={handlePhoneClick}
                  className="font-semibold text-accent border-accent hover:bg-accent hover:text-white w-full sm:w-auto shrink-0"
                >
                  <span className="truncate">0822-2664-8077</span>
                </Button>
              </div>

              {/* WhatsApp */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-white rounded-xl shadow-card border border-border">
                <div className="flex items-center w-full sm:w-auto">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <Icon name="MessageCircle" size={24} className="text-secondary" />
                  </div>
                  <div className="flex-1 sm:hidden text-left">
                    <h3 className="font-semibold text-foreground">WhatsApp 24/7</h3>
                    <p className="text-muted-foreground text-sm">Chat kapan saja, teknisi standby</p>
                  </div>
                </div>
                <div className="hidden sm:block flex-1">
                  <h3 className="font-semibold text-foreground">WhatsApp 24/7</h3>
                  <p className="text-muted-foreground text-sm">Chat kapan saja, teknisi standby</p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDirectWhatsAppClick}
                  className="font-semibold text-secondary border-secondary hover:bg-secondary hover:text-white w-full sm:w-auto shrink-0"
                >
                  Chat Sekarang
                </Button>
              </div>

              {/* Layanan Darurat */}
              <div className="flex flex-col items-center sm:flex-row sm:justify-between gap-3 p-4 bg-white rounded-xl shadow-card border border-border">
                <div className="flex flex-col sm:flex-row items-center text-center sm:text-left w-full sm:w-auto">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center sm:mr-4 shrink-0 mb-2 sm:mb-0">
                    <Icon name="Clock" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">Layanan Darurat</h3>
                    <p className="text-muted-foreground text-sm">24 jam untuk kasus mendesak</p>
                  </div>
                </div>
                <div className="w-full sm:w-auto text-center sm:text-right shrink-0">
                  <div className="text-sm font-semibold text-primary">Tersedia</div>
                  <div className="text-xs text-muted-foreground">Siaga 24/7</div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="text-center p-3 bg-white/50 rounded-lg">
                <Icon name="Shield" size={24} className="text-secondary mx-auto mb-2" />
                <div className="text-sm font-semibold text-foreground">Garansi</div>
                <div className="text-xs text-muted-foreground">1 Bulan</div>
              </div>
              <div className="text-center p-3 bg-white/50 rounded-lg">
                <Icon name="Award" size={24} className="text-primary mx-auto mb-2" />
                <div className="text-sm font-semibold text-foreground">Bersertifikat</div>
                <div className="text-xs text-muted-foreground">HVAC Pro</div>
              </div>
              <div className="text-center p-3 bg-white/50 rounded-lg col-span-2 sm:col-span-1">
                <Icon name="Users" size={24} className="text-accent mx-auto mb-2" />
                <div className="text-sm font-semibold text-foreground">Pelanggan</div>
                <div className="text-xs text-muted-foreground">250+</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-xl border border-border">
            <div className="text-center mb-8">
              <h3 className="font-heading font-bold text-2xl text-foreground mb-2">
                Dapatkan Konsultasi Gratis
              </h3>
              <p className="text-muted-foreground">
                Isi form di bawah untuk mendapatkan penawaran terbaik
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-8">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                currentStep >= 1 ? 'bg-primary text-white' : 'bg-border text-muted-foreground'
              }`}>
                1
              </div>
              <div className={`w-16 h-1 ${currentStep >= 2 ? 'bg-primary' : 'bg-border'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                currentStep >= 2 ? 'bg-primary text-white' : 'bg-border text-muted-foreground'
              }`}>
                2
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Service Selection */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <Select
                    label="Pilih Layanan yang Dibutuhkan"
                    placeholder="Pilih jenis layanan AC..."
                    options={serviceOptions}
                    value={formData?.serviceType}
                    onChange={(value) => handleInputChange('serviceType', value)}
                    required
                  />
                  
                  {formData?.serviceType && (
                    <div className="bg-primary/5 rounded-xl p-4">
                      <h4 className="font-semibold text-foreground mb-2">Layanan yang Dipilih:</h4>
                      <p className="text-sm text-muted-foreground break-words">
                        {serviceOptions?.find(s => s?.value === formData?.serviceType)?.label}
                      </p>
                    </div>
                  )}

                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!formData?.serviceType}
                    fullWidth
                    size="lg"
                    className="bg-primary hover:bg-primary/90 font-semibold"
                  >
                    Lanjutkan ke Detail Kontak
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Contact Details */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <Input
                    label="Nama Lengkap"
                    type="text"
                    placeholder="Masukkan nama lengkap Anda"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    required
                  />

                  <Input
                    label="Nomor WhatsApp"
                    type="tel"
                    placeholder="08xx-xxxx-xxxx"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    required
                  />

                  <Select
                    label="Lokasi/Area"
                    placeholder="Pilih area lokasi Anda..."
                    options={locationOptions}
                    value={formData?.location}
                    onChange={(value) => handleInputChange('location', value)}
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Pesan Tambahan (Opsional)
                    </label>
                    <textarea
                      placeholder="Ceritakan masalah AC Anda atau pertanyaan lainnya..."
                      value={formData?.message}
                      onChange={(e) => handleInputChange('message', e?.target?.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="w-full sm:w-1/2"
                    >
                      <Icon name="ArrowLeft" size={20} className="mr-2" />
                      Kembali
                    </Button>
                    <Button
                      type="submit"
                      loading={isSubmitting}
                      disabled={!formData?.name || !formData?.phone || !formData?.location}
                      className="w-full sm:w-1/2 bg-secondary hover:bg-secondary/90 font-semibold"
                    >
                      {isSubmitting ? 'Mengirim...' : 'Kirim via WhatsApp'}
                      <Icon name="Send" size={20} className="ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* Security Note */}
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center text-sm text-muted-foreground">
                <Icon name="Lock" size={16} className="mr-2 text-secondary" />
                Data Anda aman dan tidak akan dibagikan kepada pihak ketiga
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;